import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import { 
  Check, X, Eye, MapPin, Home, User, 
  Loader2, AlertTriangle, Clock, ChevronRight, Users, Shield,
  FileText, MessageSquare, Euro, Trash2, Search, Filter,
  LayoutDashboard, Building2, UserCheck, Settings
} from 'lucide-react';
import api from '../services/api';

const AdminPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const { t } = useLanguage();
  const { toast } = useToast();
  
  // State
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  
  // Data
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0, totalOnline: 0, totalUsers: 0, totalMessages: 0, totalReactions: 0 });
  const [pendingListings, setPendingListings] = useState([]);
  const [allListings, setAllListings] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  
  // Filters
  const [listingFilter, setListingFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Selected items
  const [selectedListing, setSelectedListing] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);
  
  // Delete modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [listingToDelete, setListingToDelete] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [isAuthenticated, authLoading, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, pendingRes, allListingsRes, usersRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/pending-listings'),
        api.get('/admin/all-listings'),
        api.get('/admin/all-users')
      ]);
      setStats(statsRes.data);
      setPendingListings(pendingRes.data.listings || []);
      setAllListings(allListingsRes.data.listings || []);
      setAllUsers(usersRes.data.users || []);
      setAccessDenied(false);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      if (error.response?.status === 403) {
        setAccessDenied(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (listingId) => {
    setProcessing(true);
    try {
      await api.post(`/admin/listings/${listingId}/approve`);
      setPendingListings(prev => prev.filter(l => l.id !== listingId));
      setSelectedListing(null);
      setStats(prev => ({ ...prev, pending: prev.pending - 1, approved: prev.approved + 1, totalOnline: prev.totalOnline + 1 }));
      toast({ title: 'Goedgekeurd!', description: 'De advertentie is nu online.' });
      fetchData();
    } catch (error) {
      toast({ title: 'Fout', description: error.response?.data?.detail || 'Kon niet goedkeuren', variant: 'destructive' });
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (listingId) => {
    if (!rejectionReason.trim()) {
      toast({ title: 'Reden vereist', description: 'Geef een reden voor afwijzing.', variant: 'destructive' });
      return;
    }
    setProcessing(true);
    try {
      await api.post(`/admin/listings/${listingId}/reject`, { reason: rejectionReason });
      setPendingListings(prev => prev.filter(l => l.id !== listingId));
      setSelectedListing(null);
      setRejectionReason('');
      setStats(prev => ({ ...prev, pending: prev.pending - 1, rejected: prev.rejected + 1 }));
      toast({ title: 'Afgewezen', description: 'De verhuurder is op de hoogte gebracht.' });
      fetchData();
    } catch (error) {
      toast({ title: 'Fout', description: error.response?.data?.detail || 'Kon niet afwijzen', variant: 'destructive' });
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async () => {
    if (!listingToDelete) return;
    setProcessing(true);
    try {
      await api.delete(`/admin/listings/${listingToDelete.id}`);
      toast({ title: 'Verwijderd', description: 'De advertentie is verwijderd.' });
      setDeleteModalOpen(false);
      setListingToDelete(null);
      fetchData();
    } catch (error) {
      toast({ title: 'Fout', description: 'Kon niet verwijderen', variant: 'destructive' });
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Onbekend';
    return new Date(dateString).toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-700">In behandeling</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700">Afgewezen</Badge>;
      case 'approved':
      default:
        return <Badge className="bg-emerald-100 text-emerald-700">Online</Badge>;
    }
  };

  const filteredListings = allListings.filter(listing => {
    const matchesFilter = listingFilter === 'all' || listing.status === listingFilter || (listingFilter === 'online' && (!listing.status || listing.status === 'approved'));
    const matchesSearch = !searchQuery || 
      listing.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      listing.city?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      listing.landlord?.name?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Header />
        <main className="flex-1 flex items-center justify-center pt-20">
          <Loader2 className="w-10 h-10 animate-spin text-emerald-500" />
        </main>
        <Footer />
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Header />
        <main className="flex-1 flex items-center justify-center pt-20">
          <Card className="max-w-md mx-4 border-0 shadow-lg">
            <CardContent className="p-8 text-center">
              <Shield className="w-16 h-16 mx-auto mb-4 text-red-300" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('noAccess')}</h2>
              <p className="text-gray-500 mb-6">{t('noAdminRights')}</p>
              <Button onClick={() => navigate('/')} className="bg-emerald-500 hover:bg-emerald-600">
                {t('backToHome')}
              </Button>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  const sidebarLinks = [
    { id: 'overview', label: 'Overzicht', icon: LayoutDashboard },
    { id: 'pending', label: 'In behandeling', icon: Clock, count: stats.pending },
    { id: 'listings', label: 'Alle woningen', icon: Building2, count: stats.totalOnline },
    { id: 'users', label: 'Gebruikers', icon: Users, count: stats.totalUsers },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      <Toaster />
      <main className="flex-1 pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Beheerder Dashboard</h1>
            <p className="text-gray-500 mt-1">Beheer woningen, gebruikers en goedkeuringen</p>
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar */}
            <aside className="lg:w-64 flex-shrink-0">
              <Card className="rounded-2xl border-0 shadow-lg overflow-hidden">
                <div className="bg-gradient-to-br from-slate-700 to-slate-800 p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
                      <Shield className="w-5 h-5 text-slate-700" />
                    </div>
                    <div className="text-white">
                      <p className="font-semibold">Admin Panel</p>
                      <p className="text-slate-300 text-sm">{user?.email}</p>
                    </div>
                  </div>
                </div>
                <nav className="p-2 bg-white">
                  {sidebarLinks.map((link) => (
                    <button
                      key={link.id}
                      onClick={() => setActiveTab(link.id)}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-left transition-all ${
                        activeTab === link.id
                          ? 'bg-emerald-50 text-emerald-700 font-semibold'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                      data-testid={`admin-tab-${link.id}`}
                    >
                      <div className="flex items-center">
                        <link.icon className={`w-5 h-5 mr-3 ${activeTab === link.id ? 'text-emerald-500' : 'text-gray-400'}`} />
                        {link.label}
                      </div>
                      {link.count > 0 && (
                        <Badge className={`${link.id === 'pending' ? 'bg-yellow-500' : 'bg-emerald-500'} text-white text-xs`}>
                          {link.count}
                        </Badge>
                      )}
                    </button>
                  ))}
                </nav>
              </Card>
            </aside>

            {/* Main Content */}
            <div className="flex-1 min-w-0">
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  <h2 className="text-xl font-bold text-gray-900">Overzicht</h2>
                  
                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="border-0 shadow-sm">
                      <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                          <Clock className="w-6 h-6 text-yellow-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-gray-900">{stats.pending}</p>
                          <p className="text-sm text-gray-500">In behandeling</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="border-0 shadow-sm">
                      <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                          <Check className="w-6 h-6 text-emerald-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-gray-900">{stats.totalOnline}</p>
                          <p className="text-sm text-gray-500">Online</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="border-0 shadow-sm">
                      <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                          <X className="w-6 h-6 text-red-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-gray-900">{stats.rejected}</p>
                          <p className="text-sm text-gray-500">Afgewezen</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="border-0 shadow-sm">
                      <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                          <Users className="w-6 h-6 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-gray-900">{stats.totalUsers}</p>
                          <p className="text-sm text-gray-500">Gebruikers</p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Additional Stats */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <Card className="border-0 shadow-sm">
                      <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                          <MessageSquare className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-gray-900">{stats.totalMessages}</p>
                          <p className="text-sm text-gray-500">Berichten</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="border-0 shadow-sm">
                      <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                          <FileText className="w-6 h-6 text-orange-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-gray-900">{stats.totalReactions}</p>
                          <p className="text-sm text-gray-500">Reacties</p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Quick Actions */}
                  {stats.pending > 0 && (
                    <Card className="border-0 shadow-lg rounded-2xl bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-200">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-yellow-500 rounded-xl flex items-center justify-center">
                              <AlertTriangle className="w-6 h-6 text-white" />
                            </div>
                            <div>
                              <h3 className="font-bold text-gray-900">{stats.pending} woning(en) wachten op goedkeuring</h3>
                              <p className="text-sm text-gray-600">Bekijk en beoordeel nieuwe advertenties</p>
                            </div>
                          </div>
                          <Button 
                            onClick={() => setActiveTab('pending')}
                            className="bg-yellow-500 hover:bg-yellow-600 text-white"
                          >
                            Bekijken
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}

              {/* Pending Tab */}
              {activeTab === 'pending' && (
                <div className="space-y-6">
                  <h2 className="text-xl font-bold text-gray-900">In behandeling ({stats.pending})</h2>
                  
                  <div className="grid lg:grid-cols-3 gap-6">
                    {/* Pending List */}
                    <div className="lg:col-span-1">
                      <Card className="border-0 shadow-lg rounded-2xl">
                        <CardContent className="p-0">
                          {pendingListings.length > 0 ? (
                            <div className="divide-y max-h-[600px] overflow-y-auto">
                              {pendingListings.map((listing) => (
                                <button
                                  key={listing.id}
                                  onClick={() => setSelectedListing(listing)}
                                  className={`w-full p-4 text-left hover:bg-gray-50 transition-colors ${
                                    selectedListing?.id === listing.id ? 'bg-emerald-50' : ''
                                  }`}
                                  data-testid={`pending-listing-${listing.id}`}
                                >
                                  <div className="flex items-center gap-3">
                                    <img 
                                      src={listing.images?.[0] || 'https://via.placeholder.com/60'}
                                      alt=""
                                      className="w-14 h-14 rounded-lg object-cover"
                                    />
                                    <div className="flex-1 min-w-0">
                                      <p className="font-semibold text-gray-900 truncate">{listing.title}</p>
                                      <p className="text-sm text-gray-500">{listing.city} · €{listing.price}/mnd</p>
                                      <p className="text-xs text-gray-400">{formatDate(listing.createdAt)}</p>
                                    </div>
                                    <ChevronRight className="w-5 h-5 text-gray-400" />
                                  </div>
                                </button>
                              ))}
                            </div>
                          ) : (
                            <div className="p-8 text-center text-gray-500">
                              <Check className="w-12 h-12 mx-auto mb-3 text-emerald-300" />
                              <p className="font-medium">Geen advertenties wachtend</p>
                              <p className="text-sm mt-1">Alle advertenties zijn beoordeeld</p>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </div>

                    {/* Selected Listing Detail */}
                    <div className="lg:col-span-2">
                      {selectedListing ? (
                        <Card className="border-0 shadow-lg rounded-2xl">
                          <CardContent className="p-6">
                            {/* Images */}
                            <div className="aspect-video rounded-xl overflow-hidden bg-gray-100 mb-6">
                              <img 
                                src={selectedListing.images?.[0] || 'https://via.placeholder.com/800x400'}
                                alt={selectedListing.title}
                                className="w-full h-full object-cover"
                              />
                            </div>

                            {/* Title & Badge */}
                            <div className="flex items-start justify-between mb-4">
                              <div>
                                <Badge className="bg-yellow-100 text-yellow-700 mb-2">Wachtend op goedkeuring</Badge>
                                <h2 className="text-2xl font-bold text-gray-900">{selectedListing.title}</h2>
                              </div>
                              <div className="text-right">
                                <p className="text-2xl font-bold text-emerald-600">€{selectedListing.price}</p>
                                <p className="text-gray-500">/week</p>
                              </div>
                            </div>

                            {/* Details Grid */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                              <div className="bg-gray-50 p-3 rounded-lg">
                                <p className="text-xs text-gray-500 mb-1">Type</p>
                                <p className="font-semibold text-gray-900 capitalize">{selectedListing.type}</p>
                              </div>
                              <div className="bg-gray-50 p-3 rounded-lg">
                                <p className="text-xs text-gray-500 mb-1">Locatie</p>
                                <p className="font-semibold text-gray-900">{selectedListing.city}</p>
                              </div>
                              <div className="bg-gray-50 p-3 rounded-lg">
                                <p className="text-xs text-gray-500 mb-1">Wijk</p>
                                <p className="font-semibold text-gray-900">{selectedListing.neighborhood || '-'}</p>
                              </div>
                              <div className="bg-gray-50 p-3 rounded-lg">
                                <p className="text-xs text-gray-500 mb-1">Grootte</p>
                                <p className="font-semibold text-gray-900">{selectedListing.size} m²</p>
                              </div>
                            </div>

                            {/* Description */}
                            <div className="mb-6">
                              <h3 className="font-semibold text-gray-900 mb-2">Beschrijving</h3>
                              <p className="text-gray-600">{selectedListing.description}</p>
                            </div>

                            {/* Landlord Info */}
                            <div className="bg-gray-50 p-4 rounded-xl mb-6">
                              <h3 className="font-semibold text-gray-900 mb-3">Verhuurder informatie</h3>
                              <div className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                                  <User className="w-6 h-6 text-emerald-600" />
                                </div>
                                <div>
                                  <p className="font-semibold text-gray-900">{selectedListing.landlord?.name}</p>
                                  <p className="text-sm text-gray-500">{selectedListing.landlord?.email}</p>
                                </div>
                              </div>
                            </div>

                            {/* Rejection Reason */}
                            <div className="mb-6">
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Reden voor afwijzing (verplicht bij afwijzen)
                              </label>
                              <Textarea
                                placeholder="Bijv. Onduidelijke foto's, ontbrekende informatie, verdachte prijs..."
                                value={rejectionReason}
                                onChange={(e) => setRejectionReason(e.target.value)}
                                className="min-h-[100px]"
                                data-testid="rejection-reason"
                              />
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-4">
                              <Button
                                onClick={() => handleApprove(selectedListing.id)}
                                disabled={processing}
                                className="flex-1 h-12 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold"
                                data-testid="approve-btn"
                              >
                                {processing ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Check className="w-5 h-5 mr-2" />}
                                Goedkeuren
                              </Button>
                              <Button
                                onClick={() => handleReject(selectedListing.id)}
                                disabled={processing}
                                variant="outline"
                                className="flex-1 h-12 border-red-200 text-red-600 hover:bg-red-50 font-semibold"
                                data-testid="reject-btn"
                              >
                                {processing ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <X className="w-5 h-5 mr-2" />}
                                Afwijzen
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ) : (
                        <Card className="border-0 shadow-lg rounded-2xl h-full">
                          <CardContent className="p-6 flex items-center justify-center h-full min-h-[400px]">
                            <div className="text-center text-gray-500">
                              <Eye className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                              <p className="font-medium">Selecteer een advertentie</p>
                              <p className="text-sm">Klik op een advertentie om de details te bekijken</p>
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* All Listings Tab */}
              {activeTab === 'listings' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-bold text-gray-900">Alle woningen ({allListings.length})</h2>
                  </div>
                  
                  {/* Filters */}
                  <Card className="border-0 shadow-sm rounded-xl">
                    <CardContent className="p-4">
                      <div className="flex flex-wrap gap-4">
                        <div className="flex-1 min-w-[200px]">
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <Input
                              placeholder="Zoek op titel, stad of verhuurder..."
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                              className="pl-10"
                            />
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {['all', 'pending', 'online', 'rejected'].map((filter) => (
                            <Button
                              key={filter}
                              variant={listingFilter === filter ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => setListingFilter(filter)}
                              className={listingFilter === filter ? 'bg-emerald-500 hover:bg-emerald-600' : ''}
                            >
                              {filter === 'all' && 'Alle'}
                              {filter === 'pending' && 'In behandeling'}
                              {filter === 'online' && 'Online'}
                              {filter === 'rejected' && 'Afgewezen'}
                            </Button>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Listings Table */}
                  <Card className="border-0 shadow-lg rounded-2xl overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50 border-b">
                          <tr>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Woning</th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Verhuurder</th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Prijs</th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Views</th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Datum</th>
                            <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase">Acties</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {filteredListings.map((listing) => (
                            <tr key={listing.id} className="hover:bg-gray-50" data-testid={`listing-row-${listing.id}`}>
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-3">
                                  <img 
                                    src={listing.images?.[0] || 'https://via.placeholder.com/40'}
                                    alt=""
                                    className="w-10 h-10 rounded-lg object-cover"
                                  />
                                  <div>
                                    <p className="font-medium text-gray-900 truncate max-w-[200px]">{listing.title}</p>
                                    <p className="text-sm text-gray-500">{listing.city}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <p className="text-sm text-gray-900">{listing.landlord?.name}</p>
                                <p className="text-xs text-gray-500">{listing.landlord?.email}</p>
                              </td>
                              <td className="px-6 py-4">
                                <p className="font-semibold text-emerald-600">€{listing.price}</p>
                              </td>
                              <td className="px-6 py-4">
                                {getStatusBadge(listing.status)}
                              </td>
                              <td className="px-6 py-4">
                                <p className="text-sm text-gray-600">{listing.views || 0}</p>
                              </td>
                              <td className="px-6 py-4">
                                <p className="text-sm text-gray-500">{formatDate(listing.createdAt)}</p>
                              </td>
                              <td className="px-6 py-4 text-right">
                                <div className="flex justify-end gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => window.open(`/woning/${listing.id}`, '_blank')}
                                  >
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => {
                                      setListingToDelete(listing);
                                      setDeleteModalOpen(true);
                                    }}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {filteredListings.length === 0 && (
                        <div className="p-8 text-center text-gray-500">
                          <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                          <p>Geen woningen gevonden</p>
                        </div>
                      )}
                    </div>
                  </Card>
                </div>
              )}

              {/* Users Tab */}
              {activeTab === 'users' && (
                <div className="space-y-6">
                  <h2 className="text-xl font-bold text-gray-900">Gebruikers ({allUsers.length})</h2>
                  
                  <Card className="border-0 shadow-lg rounded-2xl overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50 border-b">
                          <tr>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Gebruiker</th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Type</th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Advertenties</th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Geregistreerd</th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {allUsers.map((u) => (
                            <tr key={u.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 font-semibold">
                                    {u.name?.charAt(0).toUpperCase()}
                                  </div>
                                  <div>
                                    <p className="font-medium text-gray-900">{u.name}</p>
                                    <p className="text-sm text-gray-500">{u.email}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <Badge className={u.userType === 'verhuurder' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}>
                                  {u.userType === 'verhuurder' ? 'Verhuurder' : 'Huurder'}
                                </Badge>
                              </td>
                              <td className="px-6 py-4">
                                <p className="text-sm text-gray-600">{u.listingCount || 0}</p>
                              </td>
                              <td className="px-6 py-4">
                                <p className="text-sm text-gray-500">{formatDate(u.createdAt)}</p>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-2">
                                  {u.isAdmin && (
                                    <Badge className="bg-purple-100 text-purple-700">Admin</Badge>
                                  )}
                                  {u.verified && (
                                    <Badge className="bg-emerald-100 text-emerald-700">Geverifieerd</Badge>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Woning verwijderen
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600">
              Weet je zeker dat je <span className="font-semibold">"{listingToDelete?.title}"</span> wilt verwijderen?
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Deze actie kan niet ongedaan worden gemaakt.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
              Annuleren
            </Button>
            <Button 
              onClick={handleDelete} 
              disabled={processing}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Verwijderen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default AdminPage;
